import React, { useState, useEffect } from "react";
import {
  Popper,
  Paper,
  Typography,
  List,
  ClickAwayListener,
  IconButton,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

const AnimatedCloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 250ms ease-in-out",
  color: "#1e293b",
  maxWidth: "40px",
  maxHeight: "40px",
  "&:hover": {
    backgroundColor: "rgba(255,99,71,0.1)",
    transform: "rotate(90deg)",
    color: "#ee3349",
  },
  "&:active": {
    transform: "scale(0.9)",
  },
}));

const GradientNotificationIcon = () => (
  <>
    <svg width={0} height={0}>
      <linearGradient
        id="notificationGradient"
        x1={1}
        y1={0}
        x2={1}
        y2={1}
        gradientTransform="rotate(180)"
      >
        <stop offset="0%" stopColor="#3612C3" />
        <stop offset="100%" stopColor="#1A095D" />
      </linearGradient>
    </svg>
    <NotificationsNoneIcon sx={{ fill: "url(#notificationGradient)" }} />
  </>
);

const WhatsNewModal = ({ isOpen, onClose, anchorEl, ctx_api }) => {
  const [whatsNewItems, setwhatsNewItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isMobile = useMediaQuery("(max-width:600px)");

  const pickIcon = (name = "") => {
    const lower = name.toLowerCase();
    if (lower.includes("ai")) return "🤖";
    if (lower.includes("help")) return "🆘";
    if (lower.includes("study")) return "📚";
    if (lower.includes("notification")) return "🔔";
    if (lower.includes("performance")) return "⚡";
    if (lower.includes("course")) return "🎓";
    return "📰";
  };

  const getNewFeaturesDetails = async () => {
    try {
      const res = await ctx_api.privateRequest({
        cmd: "get_new_features_details",
      });

      console.log("API response:", res);
      const features = Array.isArray(res?.rsp?.[0]?.features)
        ? res.rsp[0].features
        : [];
      console.log("features array:", features);
      const formatted = features.map((f, index) => ({
        id: index + 1,
        feature_name: f.feature_name || "Unnamed Feature",
        description: f.description || "No description available.",
        created_on: f.created_on
          ? new Date(f.created_on).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "Unknown date",
        icon: pickIcon(f.feature_name),
        milestone: f.milestone,
      }));

      setwhatsNewItems(formatted);
    } catch (error) {
      console.error("Failed to fetch new features:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNewFeaturesDetails();
  }, [isOpen, ctx_api]);

  return (
    <>
      {loading ? (
        <CircularProgress />
      ) : (
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement={isMobile ? "bottom-end" : "left-start"}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: ({ reference, popper }) => {
                  return [0, popper.width / 32];
                },
              },
            },
          ]}
          sx={{
            zIndex: 1300,
            width: { xs: "90vw", sm: 360 },
            maxHeight: { xs: "80vh", sm: 400 },
             width: { xs: "90vw", sm: "360px" },
            maxHeight: { xs: "80vh", sm: "400px" },
            overflow: "auto",
            borderRadius: 2,
          }}
        >
          <ClickAwayListener onClickAway={onClose}>
            <Paper
              elevation={3}
              sx={{
                maxHeight: "80vh",
                overflowY: "auto",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <GradientNotificationIcon />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      ml: 1,
                      background:
                        "linear-gradient(90deg, #3612C3 0%, #1A095D 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    What's New
                  </Typography>
                </Box>
                <AnimatedCloseButton>
                  <CloseIcon fontSize="small" onClick={onClose} />
                </AnimatedCloseButton>
              </Box>
              <List
                sx={{
                  maxHeight: { xs: "60vh", sm: "350px" },
                  overflowY: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {whatsNewItems.map((item) => (
                  <Card
                    key={item.id}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      minHeight: "100px",
                      borderRadius: "8px",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          // mb: 1
                        }}
                      >
                        <Typography
                          // variant="h6"
                          sx={{
                            mr: 1,
                            fontSize: ".8rem",
                          }}
                        >
                          {item.icon}
                        </Typography>
                        <Typography
                          // variant="h6"
                          sx={{
                            fontSize: ".8rem",
                            fontWeight: 600,
                          }}
                        >
                          {item.feature_name}
                        </Typography>
                      </Box>
                      <Tooltip title={item.description} arrow>
                        <Typography
                          variant="body3"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: "block" }}
                      >
                        {item.created_on} | {item.milestone}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default WhatsNewModal;
